import * as React from 'react';
import { useRef } from 'react';

/** Material UI Packages. */
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { BarChart, LineChart } from '@mui/x-charts';

/** Material UI Icons. */
import InfoIcon from '@mui/icons-material/Info';

/** Internal Components. */
import ChartFilter from './ChartFilter';
import HeaderWithIcon from 'shared/ui/HeaderIcon';

export default function VolumeChart(props) {
    /** Get props from Analytics.js */
    const { lineChart, barChart, setLineChart, setBarChart, tradePartnerList } = props;

    /** State for the line chart and volume chart data. */
    const barRef = useRef();
    const lineRef = useRef();

    /** constants to size the charts based on screen width. */
    const theme = useTheme();
    /** Below "lg" the two Analytics panels stack, so each chart gets the full page width. */
    const isStacked = useMediaQuery(theme.breakpoints.down('lg'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isSmall = useMediaQuery('(max-width:1440px)');

    /** Chart height only (width is left responsive so it always fills its container,
     *  whether the panel is stacked full-width on tablet or half-width on desktop). */
    const lineChartHeight = isStacked ? (isMobile ? 220 : 280) : isSmall ? 250 : 280;
    const barChartHeight = isStacked ? (isMobile ? 220 : 280) : isSmall ? 250 : 300;

    return (
        <>
            <HeaderWithIcon
                icon={<InfoIcon sx={{ fontSize: 20, color: '#00647e' }} />}
                tooltipTitle={
                    'The chart is calculated based on ETD and displays the house volume. For FTL/LTL, the volume is calculated based on BOL.'
                }
                headerName={'Cargo House Volume Trend Chart'}
                sx={{ fontSize: 14, fontWeight: 500, color: '#003a52', mr: 0.5 }}
            />
            <Box
                sx={{
                    borderRadius: '10px',
                    boxShadow: '0px 1px 1px 1px rgba(0, 0, 0, 0.1)',
                    width: '100%',
                    my: 0.5,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        bgcolor: '#dfdfdf',
                        width: '100%',
                        borderTopRightRadius: '10px',
                        borderTopLeftRadius: '10px',
                        p: 1,
                        flex: '0 0 auto',
                    }}
                >
                    <ChartFilter
                        mode={'shipment'}
                        chartRef={lineRef}
                        tradePartnerList={tradePartnerList}
                        stateSetter={setLineChart}
                    />
                </Box>
                <Box ref={lineRef} sx={{ width: '100%', overflow: 'hidden' }}>
                    <LineChart
                        xAxis={[
                            {
                                scaleType: 'band',
                                data: lineChart.label,
                            },
                        ]}
                        series={[{ data: lineChart.data, color: 'rgba(0, 101, 126, 0.5)' }]}
                        height={lineChartHeight}
                        grid={{ horizontal: true }}
                    />
                </Box>
            </Box>
            <HeaderWithIcon
                icon={<InfoIcon sx={{ fontSize: 20, color: '#00647e' }} />}
                tooltipTitle={
                    'The chart is calculated based on ETD and displays the container volume for only ocean shipments.'
                }
                headerName={'Cargo Container Volume Trend Chart'}
                sx={{ fontSize: 14, fontWeight: 500, color: '#003a52', mr: 0.5 }}
            />
            <Box
                sx={{
                    borderRadius: '10px',
                    boxShadow: '0px 1px 1px 1px rgba(0, 0, 0, 0.1)',
                    width: '100%',
                    my: 0.5,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        bgcolor: '#dfdfdf',
                        width: '100%',
                        borderTopRightRadius: '10px',
                        borderTopLeftRadius: '10px',
                        p: 1,
                        flex: '0 0 auto',
                    }}
                >
                    <ChartFilter
                        mode={'container'}
                        chartRef={barRef}
                        tradePartnerList={tradePartnerList}
                        stateSetter={setBarChart}
                    />
                </Box>
                <Box ref={barRef} sx={{ width: '100%', overflow: 'hidden' }}>
                    <BarChart
                        xAxis={[
                            {
                                scaleType: 'band',
                                data: barChart.label,
                            },
                        ]}
                        series={[{ data: barChart.data, color: 'rgba(0, 101, 126, 0.5)' }]}
                        height={barChartHeight}
                        borderRadius={10}
                        grid={{ horizontal: true }}
                    />
                </Box>
            </Box>
        </>
    );
}
